// web/src/components/ds/DsComposerTextarea.tsx — DS.6 (sprint aos-ds-composer,
// spec §2 D6). ONE adapter over `@ds/data-input/Textarea` replacing the TWO
// duplicate `InputGroupTextarea` mounts `Composer.tsx` carried (one per mode),
// each with its own auto-grow effect. The duplication dies; the REASON the two
// existed (different per-mode min/max line counts) survives as props.
//
// Four measured constraints of the vendored piece live here and nowhere else:
//
//   * CLASS vs STYLE. `Textarea` destructures `className` onto its WRAPPER
//     (Textarea.tsx:99-110, mounted :113); only `style` and the rest of
//     `{...props}` reach the real `<textarea>` (:119-131). So every text-level
//     declaration the composer used to carry in `className` is inline `style`
//     here — or, better, is dropped because the piece already owns it through a
//     skin role: the text colour is `.textarea { color: var(--text-primary) }`
//     and the placeholder colour is `.textarea::placeholder { color:
//     var(--text-disabled) }` (Textarea.module.css:69-85). A `placeholder:`
//     Tailwind variant on the wrapper would style nothing, and a pseudo-element
//     cannot be styled inline at all — the skin binding IS the answer.
//     [medido at the vendored v0.2.0 tree, 2026-09-14 — the spec's §0.18 cite
//      of `--grey-500` is a v0.1.1 reading; v0.2.0 reads `--text-disabled`.]
//
//   * THE CAP MUST SCROLL. `.autoGrow .textarea { resize: none; overflow:
//     hidden }` (Textarea.module.css:91-94, `overflow: hidden` at :93). The
//     piece's own `autoResize` sets `height = scrollHeight` with NO cap, so a
//     consumer must clamp — and a clamp without an overflow override leaves a
//     long prompt CLIPPED and unreachable. `overflowY: 'auto'` is therefore
//     inline (it reaches the textarea; a class would not), and the clamp runs
//     in this file's effect, which React runs AFTER the piece's own (child
//     passive effects flush before the parent's), so ours is the last write.
//
//   * PADDING IS THE FRAME'S. `.textarea { padding: 0 }`
//     (Textarea.module.css:78) and `.bare .field { padding: 0 }` (:38-42), so
//     the 16 px the old `InputGroupTextarea` baked into its own box is now the
//     field wrapper's in `Composer.tsx`. This adapter therefore declares NO
//     padding and its min/max heights are TEXT heights (lines x line-height),
//     which is also what makes them comparable with `scrollHeight`.
//
//   * `rows` IS SWALLOWED. `rows={variant === 'autoGrow' ? 1 : rows}`
//     (Textarea.tsx:129) — the new mode's `NEW_MIN_LINES` floor cannot ride the
//     `rows` prop, so min height is carried by inline `minHeight` only.
//
//   * NEVER A `label` PROP. The piece renders `label` as a
//     `<span className={styles.label}>` (Textarea.tsx:114-116), NOT a
//     `<label htmlFor>`, so forwarding it would silently break the new mode's
//     external label association. This adapter has no such prop and forwards
//     none; `Composer.tsx` keeps its own `<label htmlFor={promptId}>`.
//
// And the one behaviour NEITHER side had (spec §0.19, D7 row 4): an IME
// composition guard on Enter. `shouldSendOnEnter` (lib/composer-keys.ts, a pure
// fenced module) reads only `{key, shiftKey}`, so a dictation or CJK candidate
// commit — which fires Enter with `isComposing === true` — used to send half a
// sentence. Recorded as a behaviour ADDITION, not a parity claim.

import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type KeyboardEvent as ReactKeyboardEvent,
  type MutableRefObject,
  type TextareaHTMLAttributes,
} from 'react';
import Textarea from '@ds/data-input/Textarea';
import { shouldSendOnEnter } from '../../lib/composer-keys';

export interface DsComposerTextareaProps
  extends Omit<
    TextareaHTMLAttributes<HTMLTextAreaElement>,
    'rows' | 'style' | 'value' | 'defaultValue' | 'onKeyDown'
  > {
  /** ALWAYS controlled (spec §4 row 7 / AC-18): the piece keeps an internal
   *  value whenever `value` is undefined, which would fork the draft. */
  value: string;
  /** Min height in LINES, per mode (`NEW_MIN_LINES` / 1). */
  minLines: number;
  /** Max height in LINES, per mode (`NEW_MAX_LINES` / `CONTINUE_MAX_LINES`). */
  maxLines: number;
  lineHeightPx: number;
  /** Coarse pointer ⇒ Enter inserts a newline and Send is the only submit. */
  isCoarsePointer: boolean;
  onSend: () => void;
  /** The autocomplete palette's key handler: returns true when it consumed
   *  the event (`useAutocomplete`), exactly as both mounts did before. */
  onKeyDownIntercept?: (e: ReactKeyboardEvent<HTMLTextAreaElement>) => boolean;
}

export const DsComposerTextarea = forwardRef<HTMLTextAreaElement, DsComposerTextareaProps>(
  function DsComposerTextarea(
    {
      value,
      minLines,
      maxLines,
      lineHeightPx,
      isCoarsePointer,
      onSend,
      onKeyDownIntercept,
      ...props
    },
    ref,
  ) {
    const innerRef = useRef<HTMLTextAreaElement | null>(null);

    const setRefs = useCallback(
      (el: HTMLTextAreaElement | null) => {
        innerRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as MutableRefObject<HTMLTextAreaElement | null>).current = el;
      },
      [ref],
    );

    const minHeight = minLines * lineHeightPx;
    const maxHeight = maxLines * lineHeightPx;

    // The clamp the piece does not do. Runs after the piece's own autoResize
    // (parent passive effects flush last), so this is the height that sticks.
    useEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${Math.min(Math.max(el.scrollHeight, minHeight), maxHeight)}px`;
    }, [value, minHeight, maxHeight]);

    function handleKeyDown(e: ReactKeyboardEvent<HTMLTextAreaElement>) {
      if (onKeyDownIntercept?.(e)) return;
      // D7 row 4 — a composition commit fires Enter; sending here would cut
      // the founder's dictated sentence in half.
      if ((e.nativeEvent as unknown as { isComposing?: boolean }).isComposing) return;
      if (!shouldSendOnEnter(e, { isCoarsePointer })) return;
      e.preventDefault();
      onSend();
    }

    return (
      <Textarea
        ref={setRefs}
        variant="autoGrow"
        bare
        value={value}
        onKeyDown={handleKeyDown}
        style={{
          minHeight,
          maxHeight,
          lineHeight: `${lineHeightPx}px`,
          // Without this the cap CLIPS instead of scrolling (:93).
          overflowY: 'auto',
        }}
        {...props}
      />
    );
  },
);

export default DsComposerTextarea;
